// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";


import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";


// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

// Moduli specifični za projekt
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalne komponente

import { Dialog } from "ui/components/Dialog";

import { getFleetVehicleOfferSalesAPI } from "services/api/fleet/vehicles/offers/api";


import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import OfferSaleSearchFilters from "../OfferSaleSearchFilters/OfferSaleSearchFilters";
import OfferSaleSearchResults from "../OfferSaleSearchResults/OfferSaleSearchResults";
import VehicleOfferSaleEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleOfferSaleEditCard/VehicleOfferSaleEditCard";
import { OfferSaleSearchPageCmp, OfferSaleSearchPageProps } from "./types";
import { FleetVehicleOfferSaleRow, FleetVehicleOfferSaleSearchFormValues } from "services/api/fleet/vehicles/offers/types";

const OfferSaleSearchPage: OfferSaleSearchPageCmp = (
    props: OfferSaleSearchPageProps
) => {

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<FleetVehicleOfferSaleRow>
    >(initialDataWithPagination);

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [offersaleId, setOfferSaleId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<FleetVehicleOfferSaleSearchFormValues>({
        search: null, offerSaleStatusId: null, offerDateFrom: null, offerDateTo: null,
        dueDateFrom: null, dueDateTo: null, totalMin: null, totalMax: null,
        contactClientId: null,
    });


    useEffect(() => {
        fetchSearchResult(searchResults.pagination, filters);
    }, []);

    const fetchSearchResult = async (pagination: Pagination, filterValues?: FleetVehicleOfferSaleSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleOfferSalesAPI(pagination, {
                offerSaleStatusId: f.offerSaleStatusId ?? undefined,
                search: f.search ?? undefined,
                offerDateFrom: f.offerDateFrom,
                offerDateTo: f.offerDateTo,
                dueDateFrom: f.dueDateFrom,
                dueDateTo: f.dueDateTo,
                totalMin: f.totalMin,
                totalMax: f.totalMax,
                contactClientId: f.contactClientId ?? undefined,
            });
            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setOfferSaleId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setOfferSaleId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        fetchSearchResult(searchResults.pagination);
        setOfferSaleId("");
        setShowAddNewModal(false);
    }, []);

    const handleSearchChange = useCallback((formValues: FleetVehicleOfferSaleSearchFormValues) => {
        setFilters(formValues);
        fetchSearchResult(searchResults.pagination, formValues);
    }, [searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        fetchSearchResult(newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        setOfferSaleId(id);
        setOpenDialog(true);
    }

    const handleOfferSaleDeleteDialogConfirm = useCallback(async () => {
        if (!offersaleId) return;

        try {
            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setOfferSaleId("");
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [offersaleId, t]);

    const handleOfferSaleDeleteDialogClose = useCallback(() => {
        setOfferSaleId("");
        setOpenDialog(false);
    }, []);

    const handleVehicleClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                FleetVehicleRoutes.FleetVehicleDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    return (
        <>

            <div className="px-0 py-6 mx-[-1px] xl:px-6">
                <OfferSaleSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite dokument klikom na 'Dodaj ponudu'" />
                ) : (
                    <OfferSaleSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onVehicleClick={handleVehicleClick}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>

            <Dialog
                open={openDialog}
                title={t(
                    "admin.finance.offer.sale.dialog.delete.title"
                )}
                message={t(
                    "admin.finance.offer.sale.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.finance.offer.sale.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.finance.offer.sale.dialog.delete.canceltext"
                )}
                onConfirm={handleOfferSaleDeleteDialogConfirm}
                onCancel={handleOfferSaleDeleteDialogClose}
            />
            <VehicleOfferSaleEditCard
                show={showAddNewModal}
                vehicleId={""}
                offerId={offersaleId}
                onSuccess={handleEditSuccess}
                onClose={() => setShowAddNewModal(false)}
            />
        </>
    );
};

OfferSaleSearchPage.route =
    FinanceRoutes.OfferSearchPageRouter;

export default OfferSaleSearchPage;
